import { Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

type Props = {
  /** Primary, prominent message (e.g. 「解析しています…」). */
  message: string;
  /** Optional secondary line explaining what's happening / why it takes time. */
  detail?: string;
};

/**
 * Full-screen "something is working" overlay. Shown during blocking async work
 * (analyze / convert / generate / restore) so the user can clearly see the app
 * is alive and not frozen — an animated spinner + indeterminate bar, not just
 * static text.
 */
export function ProcessingOverlay({ message, detail }: Props) {
  const reduce = useReducedMotion();
  return (
    // biome-ignore lint/a11y/useSemanticElements: role="status" is an ARIA live region for a transient processing overlay; <output> carries form-association semantics that don't apply here.
    <motion.div
      role="status"
      aria-live="polite"
      aria-busy="true"
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-ink)]/40 px-6 backdrop-blur-[3px]"
    >
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-sm rounded-[16px] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center shadow-[var(--shadow-lg)]"
      >
        <Loader2
          size={40}
          aria-hidden="true"
          className="mx-auto mb-4 animate-spin text-[var(--color-coral)]"
        />
        <p className="font-display text-lg font-medium text-[var(--color-ink)]">{message}</p>
        {detail && <p className="mt-1.5 text-sm text-[var(--color-ink-muted)]">{detail}</p>}

        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
          <motion.div
            className="h-full w-1/3 rounded-full bg-[var(--color-coral)]"
            animate={reduce ? { opacity: [0.4, 1, 0.4] } : { x: ["-100%", "300%"] }}
            transition={{
              duration: reduce ? 1.4 : 1.1,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
          />
        </div>

        <p className="mt-3 text-xs text-[var(--color-ink-soft)]">
          処理が完了するまで少々お待ちください
        </p>
      </motion.div>
    </motion.div>
  );
}
